import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { productService } from '../../services/productService';
import toast from 'react-hot-toast';

const { FiUpload, FiX, FiImage } = FiIcons;

const ProductForm = ({ product, onSuccess, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(
    product?.image_url || product?.image || ''
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [categories, setCategories] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      // Support both API-shaped and UI-shaped product objects
      name: product?.product_name || product?.name || '',
      description: product?.description || '',
      price:
        typeof product?.price === 'number'
          ? product.price
          : product?.price || '',
      stock:
        typeof product?.stock === 'number'
          ? product.stock
          : product?.stock || '',
      categoryId:
        product?.category_id ||
        product?.categoryId ||
        '',
    },
  });

  // Load categories from productService (with safe fallback)
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const data = await productService.getCategories();
        if (isMounted && Array.isArray(data)) {
          setCategories(data);
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
        // productService already provides a fallback, so nothing else here
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result || '');
      setValue('image', reader.result || '');
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImagePreview('');
    setSelectedFile(null);
    setValue('image', '');
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      const name = (data.name || '').trim();
      const description = (data.description || '').trim();

      const rawPrice = data.price;
      const rawStock = data.stock;
      const rawCategoryId = data.categoryId;

      // Validate name
      if (!name) {
        toast.error('Product name is required');
        setIsSubmitting(false);
        return;
      }

      // Validate & normalize price
      const price = Number(rawPrice);
      if (!Number.isFinite(price) || price <= 0) {
        toast.error('Please enter a valid price greater than 0');
        setIsSubmitting(false);
        return;
      }

      // Validate & normalize stock (default 0)
      let stock;
      if (rawStock === '' || rawStock === null || rawStock === undefined) {
        stock = 0;
      } else {
        stock = Number(rawStock);
        if (!Number.isInteger(stock) || stock < 0) {
          toast.error('Stock must be a whole number greater than or equal to 0');
          setIsSubmitting(false);
          return;
        }
      }

      // Validate & normalize category
      const categoryId = Number(rawCategoryId);
      if (!Number.isInteger(categoryId) || categoryId <= 0) {
        toast.error('Please select a valid category');
        setIsSubmitting(false);
        return;
      }

      const productData = {
        name,
        description,
        price,
        stock,
        categoryId,
        image: imagePreview || '',
        imageFile: selectedFile || null,
      };

      const existingId = product?.product_id || product?.id;

      if (existingId) {
        await productService.updateProduct(existingId, productData);
        toast.success('Product updated successfully!');
      } else {
        await productService.createProduct(productData);
        toast.success('Product created successfully!');
      }

      if (typeof onSuccess === 'function') {
        onSuccess();
      }
    } catch (error) {
      console.error('Product form submission error:', error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to save product.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Image Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Product Image
        </label>
        <div className="flex items-center space-x-4">
          {imagePreview ? (
            <div className="relative">
              <img
                src={imagePreview}
                alt="Product preview"
                className="w-32 h-32 object-cover rounded-lg"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <FiImage className="w-8 h-8 text-gray-400" />
            </div>
          )}

          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
            >
              <FiUpload className="w-4 h-4 mr-2" />
              Choose Image
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Optional. If omitted, a default/fallback image may be used.
            </p>
          </div>
        </div>
      </div>

      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Product Name
        </label>
        <input
          type="text"
          {...register('name', { required: 'Product name is required' })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          placeholder="Enter product name"
        />
        {errors.name && (
          <p className="mt-1 text-xs text-red-600">
            {errors.name.message}
          </p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          rows={4}
          {...register('description')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          placeholder="Optional description"
        />
      </div>

      {/* Price & Stock */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Price
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            {...register('price', { required: 'Price is required' })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            placeholder="0.00"
          />
          {errors.price && (
            <p className="mt-1 text-xs text-red-600">
              {errors.price.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Stock
          </label>
          <input
            type="number"
            step="1"
            min="0"
            {...register('stock')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            placeholder="0"
          />
        </div>
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Category
        </label>
        <select
          {...register('categoryId', { required: 'Category is required' })}
          className="mt-1 block w-full rounded-md border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
        >
          <option value="">Select a category</option>
          {categories.map((category) => {
            const id = category.id || category.category_id;
            const name = category.name || category.category_name || `Category ${id}`;
            return (
              <option key={id} value={id}>
                {name}
              </option>
            );
          })}
        </select>
        {errors.categoryId && (
          <p className="mt-1 text-xs text-red-600">
            {errors.categoryId.message}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting
            ? 'Saving...'
            : product?.product_id || product?.id
            ? 'Update Product'
            : 'Create Product'}
        </button>
      </div>
    </form>
  );
};

export default ProductForm;